import { Title } from '@/common/atoms/Title'
import RRuleEditor from '@/rrule/components/RRuleEditor'
import { useAuth } from '@/user/hooks/useAuth'
import {
  Alert,
  Box,
  Button,
  Collapse,
  Heading,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import {
  getDefaultDigestRRule,
  InstantNotificationType,
  NotificationGroup,
  NotificationType,
  notificationGroups,
  notificationsConfig,
  wantsNotification,
} from '@rolebase/shared/model/notifications'
import { UserMetadata } from '@rolebase/shared/model/user'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import settings from 'src/settings'
import NotificationsGroup from '../components/NotificationsGroup'
import NotificationSwitch from '../components/NotificationSwitch'
import useUserMetadata from '../hooks/useUserMetadata'

// The notifications that are a plain on/off switch, in the order of §2.8.
//
// 🔑 Derived from `notificationsConfig` rather than listed again here: a second
// list of types beside the first one is the kind of duplicate that drifts. The
// digest is the only entry without a `metadataKey` — its state lives in its
// RRULE — so it is rendered by hand, with its schedule editor.
const instantTypes = (
  Object.keys(notificationsConfig) as NotificationType[]
).filter(
  (type): type is InstantNotificationType =>
    !!notificationsConfig[type].metadataKey
)

// The switches of one section, in the same order (EMAIL-10).
const typesOfGroup = (group: NotificationGroup) =>
  instantTypes.filter((type) => notificationsConfig[type].group === group)

export default function NotificationsSettingsPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useAuth()
  const { metadata, setMetadataMany } = useUserMetadata()

  const [enabled, setEnabled] = useState(metadata?.digestRrule !== false)
  const [saving, setSaving] = useState(false)

  // 🔴 The RESOLVED state of each email, never the raw preference.
  //
  // Reading `metadata.notifX ?? false` would show five switches off to someone
  // who receives four of these emails, and the first save would cut them for
  // real. `wantsNotification` is the very function the senders ask, so the
  // screen shows what actually happens — including `meetingStart`, whose
  // default depends on when the account was created (EM-D3, EM-D6).
  //
  // ⚠️ A missing creation date is passed through on purpose rather than
  // branched on. `AppRoute` holds a `<Loading>` until the session resolves, so
  // `user` is set by the time this page mounts — but a `useState` initializer
  // NEVER runs again, so the day that guard moves, a `: false` fallback here
  // would show everything off and the first save would make it true. Handing
  // the empty date to `isLegacyAccount` reuses its own documented answer:
  // an unreadable date counts as a legacy account, which means "keep sending".
  const [instant, setInstant] = useState<
    Partial<Record<InstantNotificationType, boolean>>
  >(() =>
    Object.fromEntries(
      instantTypes.map((type) => [
        type,
        wantsNotification(
          type,
          metadata,
          user?.createdAt ?? '',
          settings.notifications.defaultsSince
        ),
      ])
    )
  )

  const [rrule, setRrule] = useState(
    () =>
      metadata?.digestRrule ||
      (user
        ? getDefaultDigestRRule(
            user.createdAt,
            settings.notifications.defaultsSince
          ).toString()
        : '')
  )

  const handleInstantChange = (
    type: InstantNotificationType,
    checked: boolean
  ) => setInstant((prev) => ({ ...prev, [type]: checked }))

  const handleSubmit = async () => {
    setSaving(true)

    // Every preference in ONE write. Six calls to the single-key setter would
    // fire six mutations and six session refreshes — and lose five of the six
    // values, each merging on the state captured at render.
    //
    // An EXPLICIT value is written even when it equals the default: that is
    // what makes the choice durable. Left unset, `meetingStart` would flip on
    // its own the day the account stops counting as a legacy one.
    const changes: Partial<UserMetadata> = {
      digestRrule: (enabled && rrule) || false,
    }
    for (const type of instantTypes) {
      const key = notificationsConfig[type].metadataKey
      if (key) changes[key] = instant[type]
    }
    await setMetadataMany(changes)

    toast({
      title: t('NotificationsSettingsModal.toastSuccess'),
      status: 'success',
    })
    setSaving(false)
  }

  if (!user) return null

  return (
    <>
      <Title>{t('NotificationsSettingsModal.heading')}</Title>

      <VStack spacing={10} align="stretch" maxW="xl">
        <Heading as="h1" size="lg">
          {t('NotificationsSettingsModal.heading')}
        </Heading>

        {/* One section per subject (EMAIL-10). The digest is left out of this
            loop: it is the only notification carrying a schedule, so it keeps
            its own section below, with its editor. */}
        {notificationGroups
          .filter((group) => group !== 'scheduled')
          .map((group) => (
            <NotificationsGroup
              key={group}
              title={t(`NotificationsSettingsModal.groups.${group}` as const)}
            >
              {typesOfGroup(group).map((type) => (
                <Box key={type}>
                  <NotificationSwitch
                    label={t(`NotificationsSettingsModal.types.${type}`)}
                    isChecked={instant[type] ?? false}
                    onChange={(checked) => handleInstantChange(type, checked)}
                  />

                  {/* EM-D27: one sentence for the two proposal lines, placed
                      under the second of them. It is what keeps someone from
                      switching off, out of weariness, the emails that make them
                      take part in governance. */}
                  {type === 'proposalClosed' && (
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      {t('NotificationsSettingsModal.proposalsConsequence')}
                    </Text>
                  )}
                </Box>
              ))}
            </NotificationsGroup>
          ))}

        <NotificationsGroup
          title={t('NotificationsSettingsModal.groups.scheduled')}
        >
          <NotificationSwitch
            label={t('NotificationsSettingsModal.digest')}
            isChecked={enabled}
            onChange={setEnabled}
          />

          <Collapse in={enabled}>
            <RRuleEditor hideStartDate value={rrule} onChange={setRrule} />
          </Collapse>

          <Collapse in={!enabled}>
            <Alert status="info">
              {t('NotificationsSettingsModal.disabledInfo')}
            </Alert>
          </Collapse>
        </NotificationsGroup>

        <Box textAlign="right">
          <Button colorScheme="blue" isLoading={saving} onClick={handleSubmit}>
            {t('common.save')}
          </Button>
        </Box>
      </VStack>
    </>
  )
}
